// src/components/StockManagement/StockUpdateModal.jsx
import React, { useState, useEffect } from 'react';
import stockManagementService from '../../services/stockManagementService';
import './StockManagement.css';
import { showNotification } from '../../utils/notification';

const StockUpdateModal = ({
  isOpen, onClose, onSave, categories, inventories
}) => {
  const [allStocks, setAllStocks] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [stockUpdateStaging, setStockUpdateStaging] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    stockId: '',
    inventoryId: '',
    qty: '1'
  });

  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadAllStocks();
    }
  }, [isOpen]);

  const loadAllStocks = async () => {
    try {
      const stocks = await stockManagementService.getAllStocks(1, 100);
      setAllStocks(stocks);
    } catch (error) {
      console.error('Error loading stocks:', error);
    }
  };

  // ✅ Warehouse → Load ALL products from that warehouse
  const handleWarehouseChange = (inventoryId) => {
    setFormData(prev => ({
      ...prev,
      inventoryId: inventoryId,
      stockId: '',
      qty: '1'
    }));
    
    if (!inventoryId) {
      setFilteredProducts([]);
      return;
    }

    const products = allStocks.filter(item => 
      item.inventory?.id === parseInt(inventoryId) && item.qty > 0
    );
    
    setFilteredProducts(products);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'inventoryId') {
      handleWarehouseChange(value);
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const getFilteredProducts = () => {
    if (!productSearchTerm) return filteredProducts;
    
    return filteredProducts.filter(product => 
      product.productName.toLowerCase().includes(productSearchTerm.toLowerCase()) ||
      product.id.toString().includes(productSearchTerm)
    );
  };

  const handleProductSelect = (product) => {
    setFormData(prev => ({ ...prev, stockId: product.id }));
    setProductSearchTerm(product.productName);
    setShowProductDropdown(false);
  };

  const handleProductSearchChange = (e) => {
    setProductSearchTerm(e.target.value);
    setShowProductDropdown(true);
    setFormData(prev => ({ ...prev, stockId: '' }));
  };

  const getSelectedProductName = () => {
    if (formData.stockId) {
      const product = filteredProducts.find(p => p.id === parseInt(formData.stockId));
      return product?.productName || '';
    }
    return productSearchTerm;
  };

  const getCurrentQty = () => {
    const selectedProduct = filteredProducts.find(p => p.id === parseInt(formData.stockId));
    return selectedProduct?.qty || 0;
  };

  const getInventoryName = (inventoryId) => {
    return inventories.find(inv => inv.id === parseInt(inventoryId))?.name || inventoryId;
  };

  const getCategoryName = (categoryId) => {
    return categories.find(cat => cat.id === parseInt(categoryId))?.name || categoryId;
  };

  const handleAddToUpdateList = (e) => {
    e.preventDefault();
    
    if (!formData.stockId || !formData.inventoryId || parseFloat(formData.qty) <= 0) {
      showNotification('Please fill all required fields', 'error');
      return;
    }

    const selectedProduct = filteredProducts.find(p => p.id === parseInt(formData.stockId));
    
    const newItem = {
      id: Date.now(),
      inventoryId: formData.inventoryId,
      categoryId: selectedProduct.category?.id,
      categoryName: selectedProduct.category?.name || 'N/A',
      productName: selectedProduct.productName,
      currentQty: selectedProduct.qty,
      addQty: parseInt(formData.qty),
      newQty: selectedProduct.qty + parseInt(formData.qty),
      stockId: parseInt(formData.stockId)
    };

    setStockUpdateStaging([...stockUpdateStaging, newItem]);
    
    // Reset only product and quantity, keep warehouse selected
    setFormData(prev => ({
      ...prev,
      stockId: '',
      qty: '1'
    }));
    setProductSearchTerm('');
    setShowProductDropdown(false);
    
    showNotification('Item added to update list');
  };

  const handleRemoveFromUpdateList = (id) => {
    setStockUpdateStaging(stockUpdateStaging.filter(item => item.id !== id));
  };

  const handleUpdateBatch = async (e) => {
    e.preventDefault();
    
    if (stockUpdateStaging.length === 0) {
      showNotification('Add items to the update list before saving!', 'error');
      return;
    }

    console.log('=== Starting Batch Update ===');
    console.log('Items to update:', stockUpdateStaging);

    setIsSaving(true);
    
    try {
      // Prepare bulk update payload
      const bulkPayload = {
        items: stockUpdateStaging.map(item => ({
          id: item.stockId,
          qty: item.addQty
        }))
      };
      
      console.log('Sending bulk payload:', bulkPayload);
      console.log('JSON payload:', JSON.stringify(bulkPayload, null, 2));
      
      // Call bulk update service method
      const response = await stockManagementService.updateStockQuantity(bulkPayload);
      console.log('Response:', response);
      console.log('=== Batch Update Complete ===');
      
      showNotification(`Successfully updated ${stockUpdateStaging.length} items!`);
      
      // ✅ Call parent to refresh table
      await onSave();
      
      // Reset on success
      setStockUpdateStaging([]);
      setFormData({
        stockId: '',
        inventoryId: '',
        qty: '1'
      });
      setProductSearchTerm('');
      setShowProductDropdown(false);
      
      onClose();
    } catch (error) {
      console.error('=== Error updating stock ===');
      console.error('Error object:', error);
      console.error('Error response:', error.response);
      console.error('Error data:', error.response?.data);
      console.error('Error message:', error.message);
      showNotification('Error updating stock: ' + (error.response?.data?.message || error.message), 'error');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="stock-out-overlay">
      <div className="stock-out-modal">
        <header className="stock-out-header">
          <h3 className="stock-out-title">Update Stock Quantity</h3>
          <button className="stock-out-close-btn" onClick={onClose} disabled={isSaving}>
            ×
          </button>
        </header>

        <div className="stock-out-body">
          <div className="stock-out-debug-box">
            <strong>Debug:</strong> Stocks loaded: {allStocks.length} | 
            Products in warehouse: {filteredProducts.length}
          </div>

          <div>
            <div className="stock-out-form-grid">
              <label className="stock-out-label">Warehouse *</label>
              <select
                name="inventoryId"
                className="stock-out-input"
                value={formData.inventoryId}
                onChange={handleChange}
                required
                disabled={isSaving}
              >
                <option value="">Select Warehouse</option>
                {inventories.map(inv => (
                  <option key={inv.id} value={inv.id}>
                    {inv.name} (ID: {inv.id})
                  </option>
                ))}
              </select>

              <label className="stock-out-label">Product Name *</label>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  className="stock-out-input"
                  placeholder={
                    !formData.inventoryId 
                      ? 'Select warehouse first' 
                      : filteredProducts.length === 0 
                        ? 'No products available' 
                        : 'Search or select product...'
                  }
                  value={getSelectedProductName()}
                  onChange={handleProductSearchChange}
                  onFocus={() => filteredProducts.length > 0 && setShowProductDropdown(true)}
                  disabled={isSaving || !formData.inventoryId || filteredProducts.length === 0}
                />
                {showProductDropdown && getFilteredProducts().length > 0 && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    maxHeight: '200px',
                    overflowY: 'auto',
                    backgroundColor: 'white',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    zIndex: 1000,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}>
                    {getFilteredProducts().map(p => (
                      <div
                        key={p.id}
                        onClick={() => handleProductSelect(p)}
                        style={{
                          padding: '10px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #eee',
                          backgroundColor: formData.stockId === p.id ? '#f0f0f0' : 'white'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = formData.stockId === p.id ? '#f0f0f0' : 'white'}
                      >
                        <div style={{ fontWeight: '500' }}>{p.productName}</div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          ID: {p.id} | Category: {p.category?.name || 'N/A'} | Current Qty: {p.qty}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <label className="stock-out-label">Current Qty</label>
              <input
                className="stock-out-input stock-out-read-only"
                readOnly
                value={getCurrentQty()}
              />

              <label className="stock-out-label">Add Quantity *</label>
              <input
                name="qty"
                type="number"
                min="1"
                className="stock-out-input"
                value={formData.qty}
                onChange={handleChange}
                required
                disabled={isSaving || !formData.stockId}
              />
            </div>

            <h4 className="stock-out-mini-log-title">Update List: Items to Update</h4>
            <div className="stock-out-mini-log-container">
              <table className="stock-out-table">
                <thead>
                  <tr className="stock-out-table-row">
                    <th className="stock-out-table-header">Warehouse</th>
                    <th className="stock-out-table-header">Category</th>
                    <th className="stock-out-table-header">Product Name</th>
                    <th className="stock-out-table-header">Current Qty</th>
                    <th className="stock-out-table-header">Add Qty</th>
                    <th className="stock-out-table-header">New Qty</th>
                    <th className="stock-out-table-header">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {stockUpdateStaging.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="stock-out-no-items-cell">No items added yet.</td>
                    </tr>
                  ) : (
                    stockUpdateStaging.map(item => (
                      <tr key={item.id} className="stock-out-table-row">
                        <td className="stock-out-table-cell">{getInventoryName(item.inventoryId)}</td>
                        <td className="stock-out-table-cell">{item.categoryName}</td>
                        <td className="stock-out-table-cell">{item.productName}</td>
                        <td className="stock-out-table-cell">{item.currentQty}</td>
                        <td className="stock-out-table-cell">+{item.addQty}</td>
                        <td className="stock-out-table-cell">{item.newQty}</td>
                        <td className="stock-out-table-cell">
                          <button
                            className="stock-out-btn-remove"
                            onClick={() => handleRemoveFromUpdateList(item.id)}
                            disabled={isSaving}
                          >
                            ×
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <footer className="stock-out-footer">
          <button 
            className="stock-out-btn stock-out-btn-cancel"
            onClick={onClose} 
            disabled={isSaving}
          >
            Close
          </button>
          <button
            className="stock-out-btn stock-out-btn-add"
            onClick={handleAddToUpdateList}
            disabled={isSaving || !formData.stockId}
          >
            Add to List
          </button>
          <button
            className="stock-out-btn stock-out-btn-save"
            onClick={handleUpdateBatch}
            disabled={isSaving || stockUpdateStaging.length === 0}
          >
            {isSaving ? 'Updating...' : 'Save Updates'}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default StockUpdateModal;